import EditTaskForm from "@/components/EditTaskForm";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import { useEntryConfigs, useEntryCollections, useExperiment, usePlots, useTask, useTrial } from "@/hooks/useFirestore";
import { useTaskRankingBundle } from "@/hooks/useTaskRankingBundle";
import { entryCollectionBelongsToPlot, usePlotNavigation } from "@/hooks/usePlotNavigation";
import { useDeleteTask, useUpdateTask } from "@/services/dataService";
import { Task } from "@/types/task";
import { Plot } from "@/types/plot";
import { EntryCollection } from "@/types/entryCollection";
import {
  ArrowLeft,
  BadgeCheck,
  Calendar,
  CalendarClock,
  Clock,
  Download,
  Edit,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import CopyLinkButton from "@/components/CopyLinkButton";
import TransitionOverlay from "@/components/TransitionOverlay";
import D3HeatmapGrid from "../components/D3HeatmapGrid";
import PlotDetailsSheet from "@/components/PlotDetailsSheet";
import {
  DEFAULT_OBJECT_REVIEW_SESSION,
  type ObjectReviewSession,
  type ViewSettingsSession,
} from "@/components/Annotations/annotationViewSettings";
import { getTraitIcon, getTraitColor, sortTraitsByTypeAndName } from "@/lib/traitIcons";
import { cn, formatTimestampDate } from "@/lib/utils";
import { MeasurementMetric, getDefaultMetric } from "@/types/measurementMetrics";
import { filterEntryConfigsForUI } from "@/constants/entryConfigVisibility";

type TaskState = "completed" | "in-progress" | "planned";

const TASK_DETAILS_PAGE_CLASS_NAME = "p-4";

const TaskDetails = () => {
  const { trialId, experimentId, taskId } = useParams<{ trialId: string; experimentId: string; taskId: string }>();
  const { userRole, selectedOrganization } = useAuth();
  const navigate = useNavigate();
  const paths = useOrgPaths();
  const beginNavigationGuard = useNavigationGuard();
  const { toast } = useToast();
  const { trial, loading: trialLoading, error: trialError } = useTrial(trialId);
  const { task, loading: taskLoading, error: taskError } = useTask(trialId, experimentId, taskId);
  const { entryConfigs, loading: entryConfigsLoading, error: entryConfigsError } = useEntryConfigs();
  const { plots } = usePlots(trialId, experimentId);
  // Owned here, not in each child: the heatmap needs the whole experiment for
  // exports and the plot details sheet needs its plot map columns, and a single
  // subscription keeps the two from rendering off different snapshots.
  const { experiment, loading: experimentLoading, error: experimentError } = useExperiment(trialId, experimentId);
  const { entryCollections, loading: entryCollectionsLoading } = useEntryCollections(trialId, experimentId, taskId);
  // W5: surface a "View AI Traits" entry point only when a ready ranking bundle exists for this task.
  const { bundle: aiTraitsBundle } = useTaskRankingBundle(trialId, experimentId, taskId);
  const deleteTask = useDeleteTask();
  const updateTask = useUpdateTask();

  // Role-based permissions
  // Collectors, editors, and admins can see edit UI (collector save restrictions enforced in EditTaskForm)
  const canEdit = userRole && ["collector", "editor", "admin"].includes(userRole);
  // Collectors, editors, and admins can see delete UI (actual deletion enforced by backend security rules)
  const canDelete = userRole && ["collector", "editor", "admin"].includes(userRole);

  const [selectedEntryConfigId, setSelectedEntryConfigId] = useState<string | null>(null);
  const [selectedMeasurementMetric, setSelectedMeasurementMetric] = useState<MeasurementMetric | null>(null);
  const measurementMetricSelectionRef = useRef<{
    contextKey: string | null;
    dataContextKey: string | null;
    userSelected: boolean;
    entryCollectionsResolved: boolean;
    staleEntryCollections?: EntryCollection[];
  }>({
    contextKey: null,
    dataContextKey: null,
    userSelected: false,
    entryCollectionsResolved: false,
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEditTaskDirty, setIsEditTaskDirty] = useState(false);
  // Warn before discarding in-progress task edits (Escape, click-outside, X, Cancel).
  const editTaskGuard = useUnsavedChangesGuard({
    isDirty: isEditTaskDirty,
    onOpenChange: setIsEditDialogOpen,
  });
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState<{
    plot: Plot;
    entryCollections: EntryCollection[];
  } | null>(null);
  // Session-level answer to the annotation-viewer "keep these view settings?" prompt.
  // Lives here (not in the sheet) so it survives sheet close/reopen; clears on route change.
  const [viewSettingsSession, setViewSettingsSession] = useState<ViewSettingsSession>({
    choice: "unasked",
    settings: null,
  });
  // Session-level answers to the viewer's review-mode prompts, reset on the same identity change
  // as the view settings above. Kept separate because that session object is rebuilt from fresh
  // literals at several call sites, any of which would silently drop an extra field.
  const [objectReviewSession, setObjectReviewSession] = useState<ObjectReviewSession>(
    DEFAULT_OBJECT_REVIEW_SESSION
  );
  // React Router reuses this component instance when only the task route params change,
  // and switching the selected trait doesn't change the route at all. An org switch from
  // the header now goes through App's switch gate and remounts the org-keyed main shell,
  // but a copied trial can share IDs across orgs, so the org id stays in the deps as a
  // guard for any org change made in place. Any of these could otherwise carry one
  // context's persist choice + model-specific thresholds into the next and silently
  // apply/save them. Reset the session whenever the task, trait, OR organization identity
  // changes.
  useEffect(() => {
    setViewSettingsSession({ choice: "unasked", settings: null });
    setObjectReviewSession(DEFAULT_OBJECT_REVIEW_SESSION);
  }, [selectedOrganization?.id, trialId, experimentId, taskId, selectedEntryConfigId]);
  const [deletionOverlay, setDeletionOverlay] = useState<{
    open: boolean;
    title: string;
    subtitle?: string;
  } | null>(null);

  // Filter EntryConfigs to only those referenced in the task's dataCollectionConfigs
  const availableEntryConfigs = useMemo(() => {
    if (!entryConfigs.length || !task?.dataCollectionConfigs?.length) {
      return [];
    }

    const visibleEntryConfigs = filterEntryConfigsForUI(entryConfigs);

    return visibleEntryConfigs.filter((config) => {
      const configId = config.id;
      return task.dataCollectionConfigs?.some((dataConfig) => {
        const refId =
          typeof dataConfig.entryConfigRef === "string" ? dataConfig.entryConfigRef : dataConfig.entryConfigRef.id;
        return refId === configId;
      });
    });
  }, [entryConfigs, task?.dataCollectionConfigs]);

  // Set the first available EntryConfig as selected once data loads
  useEffect(() => {
    if (!entryConfigsLoading && availableEntryConfigs.length > 0 && selectedEntryConfigId === null) {
      setSelectedEntryConfigId(availableEntryConfigs[0].id!);
    }
  }, [entryConfigsLoading, availableEntryConfigs, selectedEntryConfigId]);

  // If the trait shown in the Plot Map View is removed from the task, the
  // selected id would point at a now-missing trait and the dropdown would show
  // blank. Fall back to the first remaining trait (or none) instead.
  useEffect(() => {
    if (
      selectedEntryConfigId !== null &&
      !availableEntryConfigs.some((config) => config.id === selectedEntryConfigId)
    ) {
      setSelectedEntryConfigId(availableEntryConfigs.length > 0 ? availableEntryConfigs[0].id! : null);
    }
  }, [availableEntryConfigs, selectedEntryConfigId]);

  const defaultMeasurementMetric = useMemo(() => {
    const currentEntryConfig = availableEntryConfigs.find((config) => config.id === selectedEntryConfigId);
    return currentEntryConfig ? getDefaultMetric(currentEntryConfig, entryCollections) : null;
  }, [availableEntryConfigs, entryCollections, selectedEntryConfigId]);

  const measurementMetricDataContextKey = JSON.stringify([
    selectedOrganization?.id ?? null,
    trialId ?? null,
    experimentId ?? null,
    taskId ?? null,
  ]);
  const measurementMetricContextKey = selectedEntryConfigId
    ? JSON.stringify([measurementMetricDataContextKey, selectedEntryConfigId])
    : null;

  // Apply a provisional default when the selected trait changes, then refine it
  // once when that task's entry collections first resolve. Later live snapshots
  // must not reset an explicit choice (or keep changing the selected tab).
  useEffect(() => {
    if (!measurementMetricContextKey) return;

    let selection = measurementMetricSelectionRef.current;
    const isNewContext = selection.contextKey !== measurementMetricContextKey;
    const hadPreviousContext = selection.contextKey !== null;
    if (isNewContext) {
      const dataContextChanged =
        selection.dataContextKey !== null && selection.dataContextKey !== measurementMetricDataContextKey;
      const staleEntryCollections = dataContextChanged
        ? entryCollections
        : !selection.entryCollectionsResolved
          ? selection.staleEntryCollections
          : undefined;
      selection = {
        contextKey: measurementMetricContextKey,
        dataContextKey: measurementMetricDataContextKey,
        userSelected: false,
        entryCollectionsResolved: false,
        ...(staleEntryCollections ? { staleEntryCollections } : {}),
      };
      measurementMetricSelectionRef.current = selection;
    }

    const isFreshEntryCollectionsSnapshot =
      !entryCollectionsLoading &&
      (!selection.staleEntryCollections || selection.staleEntryCollections !== entryCollections);
    const isFirstResolvedSnapshot = isFreshEntryCollectionsSnapshot && !selection.entryCollectionsResolved;
    if (isFirstResolvedSnapshot) {
      selection.entryCollectionsResolved = true;
      delete selection.staleEntryCollections;
    }

    if (
      defaultMeasurementMetric &&
      ((isNewContext && !hadPreviousContext) || (isFirstResolvedSnapshot && !selection.userSelected))
    ) {
      setSelectedMeasurementMetric(defaultMeasurementMetric);
    }
  }, [
    defaultMeasurementMetric,
    entryCollections,
    entryCollectionsLoading,
    measurementMetricContextKey,
    measurementMetricDataContextKey,
  ]);

  const handleMeasurementMetricChange = (metric: MeasurementMetric) => {
    const selection = measurementMetricSelectionRef.current;
    if (selection.contextKey === measurementMetricContextKey) {
      selection.userSelected = true;
    } else {
      measurementMetricSelectionRef.current = {
        contextKey: measurementMetricContextKey,
        dataContextKey: measurementMetricDataContextKey,
        userSelected: true,
        entryCollectionsResolved: !entryCollectionsLoading,
      };
    }
    setSelectedMeasurementMetric(metric);
  };

  // Define handlePlotSelect before the navigation hook (needs to be stable for hook dependency)
  const handlePlotSelect = (plot: Plot, plotEntryCollections: EntryCollection[]) => {
    setSelectedPlot({ plot, entryCollections: plotEntryCollections });
    setIsSheetOpen(true);
  };

  // Plot navigation hook - must be called before early returns
  const navigation = usePlotNavigation({
    plots,
    entryCollections,
    currentPlot: selectedPlot?.plot ?? null,
    onNavigate: handlePlotSelect,
  });
  const selectedPlotEntryCollections = useMemo(
    () =>
      selectedPlot
        ? entryCollections.filter((entryCollection) => entryCollectionBelongsToPlot(entryCollection, selectedPlot.plot))
        : [],
    [entryCollections, selectedPlot]
  );

  // Show loading state
  if (trialLoading || experimentLoading || taskLoading || entryConfigsLoading) {
    return (
      <div data-testid="task-details-page" className={TASK_DETAILS_PAGE_CLASS_NAME}>
        Loading...
      </div>
    );
  }

  // Show error state
  if (trialError || experimentError || taskError || entryConfigsError) {
    return (
      <div data-testid="task-details-page" className={TASK_DETAILS_PAGE_CLASS_NAME}>
        Error: {trialError?.message || experimentError?.message || taskError?.message || entryConfigsError?.message}
      </div>
    );
  }

  if (!trial || !task) {
    return (
      <Navigate
        replace
        to={paths.experiment(trialId, experimentId)}
      />
    );
  }

  const handleDeleteTask = async () => {
    if (!trialId || !experimentId || !taskId) {
      return;
    }

    // Don't manually close the dialog here - let Radix's AlertDialogAction
    // handle the close via onOpenChange to ensure proper body pointer-events cleanup.
    setDeletionOverlay({
      open: true,
      title: "Deleting task...",
      subtitle: `Removing "${task.name}"...`,
    });

    const stillHere = beginNavigationGuard();
    try {
      await deleteTask(trialId, experimentId, taskId);
      toast({
        title: "Task deleted",
        description: `${task.name} has been deleted`,
      });

      // Navigate back to the experiment page, unless the user already went elsewhere
      if (stillHere()) {
        navigate(paths.experiment(trialId, experimentId));
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete task",
        variant: "destructive",
      });
    } finally {
      setDeletionOverlay(null);
    }
  };

  const handleEditTask = async (updatedTask: Task) => {
    if (!trialId || !experimentId || !updatedTask.id) return;

    try {
      await updateTask(trialId, experimentId, updatedTask.id, updatedTask);
      toast({
        title: "Task updated",
        description: `${updatedTask.name} has been updated`,
      });

      // Close the dialog
      setIsEditDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update task",
        variant: "destructive",
      });
    }
  };

  const handleEntryConfigChange = (entryConfigId: string) => {
    setSelectedEntryConfigId(entryConfigId);
  };

  const handleSheetClose = () => {
    setIsSheetOpen(false);
    setTimeout(() => setSelectedPlot(null), 300);
  };

  const handleStateChange = async (newState: TaskState) => {
    if (!trialId || !experimentId || !taskId || !task || task.state === newState) return;

    try {
      // Send ONLY the changed field — never the (possibly stale) full task.
      // A whole-task write here would rewrite dataCollectionConfigs and could
      // silently strand (hide) a concurrently-added trait's collected data.
      // updateTask does a partial updateDoc, so this leaves
      // dataCollectionConfigs untouched.
      await updateTask(trialId, experimentId, taskId, { state: newState });
      toast({
        title: "Task state updated",
        description: `Task state changed to ${newState}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update task state",
        variant: "destructive",
      });
    }
    setIsStateDropdownOpen(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <BadgeCheck className="h-5 w-5 text-green-600" />;
      case "in-progress":
        return <Clock className="h-5 w-5 text-amber-500" />;
      case "planned":
        return <CalendarClock className="h-5 w-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-50 text-green-700 border-green-200";
      case "in-progress":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "planned":
        return "bg-blue-50 text-blue-700 border-blue-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const selectedEntryConfig = availableEntryConfigs.find((config) => config.id === selectedEntryConfigId);

  return (
    <div data-testid="task-details-page" className={TASK_DETAILS_PAGE_CLASS_NAME}>
      <TransitionOverlay
        open={deletionOverlay?.open ?? false}
        title={deletionOverlay?.title ?? ""}
        subtitle={deletionOverlay?.subtitle}
      />
      <div className="mb-4">
        <Link to={paths.experiment(trialId, experimentId)}>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 mb-2 text-agri-green hover:text-agri-green-dark">
            <ArrowLeft className="h-4 w-4" />
            Back to Experiment
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-baseline justify-between pb-2">
          <div>
            <p className="text-sm text-gray-500 mb-1">{trial.name}</p>
            <CardTitle className="text-xl font-bold text-agri-green-dark">{task.name}</CardTitle>
          </div>

          <div className="flex flex-shrink-0 flex-col items-end gap-2">
            <div className="flex items-center gap-1">
              <CopyLinkButton path={paths.task(trialId, experimentId, taskId)} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canEdit && (
                    <DropdownMenuItem onClick={() => setIsEditDialogOpen(true)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setIsDownloadDialogOpen(true)}>
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </DropdownMenuItem>
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setIsDeleteDialogOpen(true)}
                        className="text-destructive focus:text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center gap-2">
              {getStatusIcon(task.state)}
              {canEdit ? (
                <DropdownMenu
                  open={isStateDropdownOpen}
                  onOpenChange={setIsStateDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`text-xs px-2 py-1 rounded-full cursor-pointer hover:shadow-md hover:scale-105 transition-all border ${getStatusClass(task.state)}`}>
                      {task.state === "in-progress"
                        ? "In Progress"
                        : task.state.charAt(0).toUpperCase() + task.state.slice(1)}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => handleStateChange("in-progress")}
                      className="cursor-pointer">
                      <Clock className="h-4 w-4 mr-2 text-amber-500" />
                      In Progress
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleStateChange("completed")}
                      className="cursor-pointer">
                      <BadgeCheck className="h-4 w-4 mr-2 text-green-600" />
                      Completed
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <span className={`text-xs px-2 py-1 rounded-full border ${getStatusClass(task.state)}`}>
                  {task.state === "in-progress"
                    ? "In Progress"
                    : task.state.charAt(0).toUpperCase() + task.state.slice(1)}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-4">{task.description}</p>

          <div className="flex items-center mb-4">
            <Calendar className="h-5 w-5 text-agri-green mr-2" />
            <span className="text-gray-700">
              {formatTimestampDate(task.startDate, "No start date")}
            </span>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-medium mb-2">Traits Measured:</h3>
            <div className="flex flex-wrap gap-2">
              {sortTraitsByTypeAndName(availableEntryConfigs).map((entryConfig) => {
                const Icon = getTraitIcon(entryConfig.type);
                const traitColor = getTraitColor(entryConfig.type);
                return (
                  <Badge
                    key={entryConfig.id}
                    variant="outline"
                    className="bg-agri-green-light/10 flex items-center gap-1.5">
                    <Icon
                      className={cn("h-3.5 w-3.5", traitColor)}
                      aria-hidden="true"
                    />
                    <span>{entryConfig.name}</span>
                  </Badge>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Heatmap Visualization */}
      <div className="mt-4">
        <D3HeatmapGrid
          task={task}
          trial={trial}
          trialId={trialId}
          experimentId={experimentId}
          availableEntryConfigs={availableEntryConfigs}
          selectedEntryConfigId={selectedEntryConfigId}
          onEntryConfigChange={handleEntryConfigChange}
          experiment={experiment}
          onPlotSelect={handlePlotSelect}
          measurementMetric={selectedMeasurementMetric}
          onMeasurementMetricChange={handleMeasurementMetricChange}
          isDownloadDialogOpen={isDownloadDialogOpen}
          onDownloadDialogOpenChange={setIsDownloadDialogOpen}
          taskId={taskId}
          aiTraitsBundleReady={!!aiTraitsBundle}
        />
      </div>

      {/* Plot Details Dialog */}
      {selectedPlot && selectedEntryConfigId && (
        <PlotDetailsSheet
          open={isSheetOpen}
          onClose={handleSheetClose}
          plot={selectedPlot.plot}
          entryCollections={selectedPlotEntryCollections}
          trialId={trialId}
          experimentId={experimentId}
          taskId={taskId}
          selectedTraitName={selectedEntryConfig?.name || ""}
          selectedTraitId={selectedEntryConfigId}
          allEntryConfigs={availableEntryConfigs}
          plotMap={experiment?.plotMap}
          navigation={isSheetOpen ? navigation : null}
          viewSettingsSession={viewSettingsSession}
          onViewSettingsSessionChange={setViewSettingsSession}
          objectReviewSession={objectReviewSession}
          onObjectReviewSessionChange={setObjectReviewSession}
        />
      )}

      {/* Edit Task Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={editTaskGuard.guardedOnOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-6 pb-6">
            <EditTaskForm
              task={task}
              onSubmit={handleEditTask}
              onCancel={editTaskGuard.requestClose}
              onDirtyChange={setIsEditTaskDirty}
            />
          </div>
        </DialogContent>
      </Dialog>

      {editTaskGuard.confirmation}

      {/* Delete Task Confirmation */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? All data associated with this task will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTask}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaskDetails;
